import { useState, useEffect, useMemo } from 'react'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { Ticket } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadTickets } from '../lib/api'
import CreateTicketForm from '../components/CreateTicketForm'
import TicketList from '../components/TicketList'
import TicketsTable from '../components/TicketsTable'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import { useAlert } from '../contexts/AlertContext'
import './RequesterView.css'

function RequesterView() {
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100
  const [currentPage, setCurrentPage] = useState(1)
  const [newPage, setNewPage] = useState(1)
  const [inProgressPage, setInProgressPage] = useState(1)
  const [completedPage, setCompletedPage] = useState(1)

  const loadTicketsData = async () => {
    if (!user) return

    try {
      setLoading(true)
      // Загружаем все тикеты, но фильтруем только те, где пользователь является заявителем
      // Это важно, чтобы даже администраторы видели только свои заявки во вкладке заявителя
      const data = await loadTickets(user.id, 'requester', user.admin_type)
      // Фильтруем только те тикеты, где текущий пользователь является requester_id
      const myTickets = data.filter(t => t.requester_id === user.id)
      setTickets(myTickets)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí. Zkuste to prosím znovu.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Ждем, пока пользователь и его данные будут загружены
    if (user && !authLoading) {
      loadTicketsData()
    } else if (!authLoading && !user) {
      setLoading(false)
    }
  }, [user, authLoading])

  useDeepLinkById({
    items: tickets,
    ready: !loading,
    onFound: (t) => {
      setHighlightId(t.id)
      if (t.status === 'in_progress' || t.status === 'completed' || t.status === 'new') {
        setStatusFilter(t.status)
      } else {
        setStatusFilter(null)
      }
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již není ve vaší frontě.', 'error'),
    elementIdFor: (id) => `ticket-${id}`,
  })

  // Фильтрация тикетов по выбранному статусу
  const filteredTickets = useMemo(() => {
    return statusFilter
      ? tickets.filter(t => t.status === statusFilter)
      : tickets
  }, [tickets, statusFilter])

  const newTickets = useMemo(() => filteredTickets.filter(t => t.status === 'new'), [filteredTickets])
  const inProgressTickets = useMemo(() => filteredTickets.filter(t => t.status === 'in_progress'), [filteredTickets])
  const completedTickets = useMemo(() => filteredTickets.filter(t => t.status === 'completed'), [filteredTickets])

  // Сброс страницы при изменении фильтра
  useEffect(() => {
    setNewPage(1)
    setInProgressPage(1)
    setCompletedPage(1)
    setCurrentPage(1)
  }, [statusFilter])

  // Пагинация для каждого статуса
  const getPaginatedTickets = (ticketList: Ticket[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return ticketList.slice(startIndex, endIndex)
  }

  const getTotalPages = (ticketList: Ticket[]) => {
    return Math.ceil(ticketList.length / itemsPerPage)
  }

  const handleTicketCreated = async () => {
    setShowCreateForm(false)
    // Небольшая задержка для того, чтобы БД успела обновиться
    await new Promise(resolve => setTimeout(resolve, 500))
    await loadTicketsData()
  }

  // Показываем загрузку, пока проверяем аутентификацию или загружаем данные
  if (authLoading || loading || !user) {
    return (
      <div className="requester-view">
        <div className="loading">Načítání...</div>
      </div>
    )
  }

  if (showCreateForm) {
    return (
      <div className="requester-view">
        <CreateTicketForm
          onCancel={() => setShowCreateForm(false)}
          onSuccess={handleTicketCreated}
        />
      </div>
    )
  }

  return (
    <div className="requester-view">
      <div className="requester-header">
        <button
          className="create-ticket-button"
          onClick={() => setShowCreateForm(true)}
        >
          + Vytvořit novou žádost
        </button>
      </div>

      {!showCreateForm && !loading && (
        <>
          <div className="admin-stats">
            <div
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Nové žádosti</h3>
              <p className="stat-number">{tickets.filter(t => t.status === 'new').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              style={{ cursor: 'pointer' }}
            >
              <h3>V řešení</h3>
              <p className="stat-number">{tickets.filter(t => t.status === 'in_progress').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Dokončeno</h3>
              <p className="stat-number">{tickets.filter(t => t.status === 'completed').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer' }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{tickets.length}</p>
            </div>
          </div>
        </>
      )}

      {!showCreateForm && !loading && (
        <div className="view-controls">
          <button
            className={`view-button ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            📋 Karty
          </button>
          <button
            className={`view-button ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            📊 Tabulka
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">Načítání...</div>
      ) : showCreateForm ? null : viewMode === 'table' ? (
        <>
          <TicketsTable tickets={getPaginatedTickets(filteredTickets, currentPage)} filterType="requester" />
          {filteredTickets.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={getTotalPages(filteredTickets)}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredTickets.length}
            />
          )}
        </>
      ) : (
        <>
          {statusFilter === 'new' ? (
            <div className="tickets-section">
              <h2>Nové žádosti ({tickets.filter(t => t.status === 'new').length})</h2>
              {newTickets.length > 0 ? (
                <>
                  <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(newTickets, newPage)} />
                  {getTotalPages(newTickets) > 1 && (
                    <Pagination
                      currentPage={newPage}
                      totalPages={getTotalPages(newTickets)}
                      onPageChange={setNewPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={newTickets.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné nové žádosti</p>
              )}
            </div>
          ) : statusFilter === 'in_progress' ? (
            <div className="tickets-section">
              <h2>Probíhající žádosti ({tickets.filter(t => t.status === 'in_progress').length})</h2>
              {inProgressTickets.length > 0 ? (
                <>
                  <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(inProgressTickets, inProgressPage)} />
                  {getTotalPages(inProgressTickets) > 1 && (
                    <Pagination
                      currentPage={inProgressPage}
                      totalPages={getTotalPages(inProgressTickets)}
                      onPageChange={setInProgressPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={inProgressTickets.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti v řešení</p>
              )}
            </div>
          ) : statusFilter === 'completed' ? (
            <div className="tickets-section">
              <h2>Dokončené žádosti ({tickets.filter(t => t.status === 'completed').length})</h2>
              {completedTickets.length > 0 ? (
                <>
                  <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(completedTickets, completedPage)} />
                  {getTotalPages(completedTickets) > 1 && (
                    <Pagination
                      currentPage={completedPage}
                      totalPages={getTotalPages(completedTickets)}
                      onPageChange={setCompletedPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={completedTickets.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
              )}
            </div>
          ) : (
            <>
              <div className="tickets-section">
                <h2>Nové žádosti ({tickets.filter(t => t.status === 'new').length})</h2>
                {newTickets.length > 0 ? (
                  <>
                    <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(newTickets, newPage)} />
                    {getTotalPages(newTickets) > 1 && (
                      <Pagination
                        currentPage={newPage}
                        totalPages={getTotalPages(newTickets)}
                        onPageChange={setNewPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={newTickets.length}
                      />
                    )}
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné nové žádosti</p>
                )}
              </div>

              <div className="tickets-section">
                <h2>Probíhající žádosti ({tickets.filter(t => t.status === 'in_progress').length})</h2>
                {inProgressTickets.length > 0 ? (
                  <>
                    <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(inProgressTickets, inProgressPage)} />
                    {getTotalPages(inProgressTickets) > 1 && (
                      <Pagination
                        currentPage={inProgressPage}
                        totalPages={getTotalPages(inProgressTickets)}
                        onPageChange={setInProgressPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={inProgressTickets.length}
                      />
                    )}
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné žádosti v řešení</p>
                )}
              </div>

              <div className="tickets-section">
                <h2>Dokončené žádosti ({tickets.filter(t => t.status === 'completed').length})</h2>
                {completedTickets.length > 0 ? (
                  <>
                    <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(completedTickets, completedPage)} />
                    {getTotalPages(completedTickets) > 1 && (
                      <Pagination
                        currentPage={completedPage}
                        totalPages={getTotalPages(completedTickets)}
                        onPageChange={setCompletedPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={completedTickets.length}
                      />
                    )}
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default RequesterView
